import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import services from 'api/services';
import { ObjectException } from 'global/errors';
import { useFreecodeContextData } from 'hooks/useContext';
import DropdownButton from 'components/Dropdown/DropdownButton';
import { buttonIds } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import ConfirmModal from 'components/Modals/confirmModal';

const { FreecodeCreatorService } = services;

const labels = {
  publish: 'Publish',
  unPublish: 'Un-Publish',
  archive: 'Archive',
  unArchive: 'Un-Archive',
};

const publishText = 'Are you sure you want to publish?';
const archiveText = 'Are you sure you want to archive?';
const makeCustomText = 'Are you sure you want to return this to custom WIP state?';

const modalText = {
  [labels.publish]: publishText,
  [labels.unPublish]: makeCustomText,
  [labels.archive]: archiveText,
  [labels.unArchive]: makeCustomText,
};

export default function PublishButton() {
  const { test, setTest } = useFreecodeContextData();

  const [loading, setLoading] = useState(false);
  const [dropdownState, setDropdownState] = useState({ currentOption: { label: '' }, options: [] });
  const [modalData, setModalData] = useState(undefined);

  useEffect(() => {
    let newDropdownState = {
      currentOption: { label: labels.publish },
      options: [
        { label: labels.publish },
        { label: labels.unPublish },
        { label: labels.archive },
        { label: labels.unArchive },
      ],
    };
    const filterPublishOptions = (display, disabledOptions) => {
      newDropdownState.currentOption = { label: display };
      newDropdownState.options = newDropdownState.options.filter((option) => !disabledOptions.includes(option.label));
    };
    if (!test.custom && !test.archived) {
      filterPublishOptions('Archive', ['Publish', 'Un-Archive']);
    } else if (test.archived) {
      filterPublishOptions('Publish', ['Archive', 'Un-Publish']);
    } else if (test.custom) {
      filterPublishOptions('Publish', ['Un-Archive', 'Un-Publish']);
    }
    setDropdownState((oldState) => ({ ...oldState, ...newDropdownState }));
  }, [test?.archived, test?.custom]);

  const handlePublishStatus = (option) => {
    if (loading) return toast.error('Already loading');
    setLoading(true);

    const doConfirm = async () => {
      try {
        let newState = {};
        switch (option.label) {
          case labels.publish:
            newState.custom = false;
            newState.archived = false;
            break;
          case labels.archive:
            newState.custom = false;
            newState.archived = true;
            break;
          case labels.unPublish: // fall-through
          case labels.unArchive:
            newState.custom = true;
            newState.archived = false;
            break;
          default:
        }

        let response = await FreecodeCreatorService.handlePublishStatus(test._id, newState.custom, newState.archived);
        if (response.error)
          throw new ObjectException(response.error?.message || `${modalData.label} update failed`, 'response error');
        if (typeof response.custom !== 'boolean' || typeof response.archived !== 'boolean')
          throw new ObjectException(
            `${modalData.label} response does not contain expected bool values`,
            'response error'
          );

        setTest((oldTest) => ({ ...oldTest, custom: response.custom, archived: response.archived }));

        if (response.custom !== newState.custom || response.archived !== newState.archived)
          throw new ObjectException('Response status does not match requested status', 'response error');

        toast.success('Publish status updated');
        setModalData(undefined);
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'There was an error updating publish status');
      } finally {
        setLoading(false);
      }
    };
    doConfirm();
  };

  return (
    <>
      <DropdownButton
        id={buttonIds.publishDropdown}
        leftDrop={true}
        title="Handle the publish status of the test"
        value={dropdownState.currentOption}
        options={dropdownState.options}
        handleClick={setModalData}
      />
      {modalData && (
        <ConfirmModal
          title={modalData.label}
          text={modalText[modalData.label]}
          onCancel={() => !loading && setModalData(undefined)}
          onConfirm={() => handlePublishStatus(modalData)}
          loading={loading}
        />
      )}
    </>
  );
}
